/**
 * ICE Server Configuration
 * Fetches STUN/TURN credentials dynamically from the calling service.
 * Falls back to public STUN servers if the fetch fails.
 */

import logger from '../services/logger';
import { store } from '../store/store';
import { getAudioFlags } from '../services/abTest/variant';
import {authenticatedFetch, getAccessToken} from '../services/authRuntime';

export interface IceServer {
  urls: string[];
  username?: string;
  credential?: string;
}

export interface TurnServerCredentials {
  password: string;
  username: string;
  ttl: number;
  uris: string[];
}

const PUBLIC_STUN_FALLBACK: IceServer[] = [
  { urls: ['stun:stun.l.google.com:19302'] },
  { urls: ['stun:stun1.l.google.com:19302'] },
  { urls: ['stun:stun.cloudflare.com:3478'] },
];

let cachedIceServers: IceServer[] | null = null;
let cacheExpiryMs = 0;

/**
 * Warm up ICE server cache. Call this at UA startup so credentials are
 * available immediately when an incoming call arrives.
 */
export function prefetchIceServers(): void {
  getIceServers().catch(() => {/* errors already logged inside getIceServers */});
}

/**
 * Invalidate the TURN credential cache so the next call fetches fresh credentials.
 * Call this when a call ends so back-to-back calls don't share the same TURN
 * allocation slot (Cloudflare limits concurrent allocations per credential).
 */
export function clearIceServerCache(): void {
  cachedIceServers = null;
  cacheExpiryMs = 0;
}

/**
 * Returns true if the ICE candidate handler should assume TURN is available
 * and wait for relay candidates before releasing. When caching/prefetch is
 * disabled (variant A) we still fetch TURN on demand in `getIceServers()`,
 * so TURN is effectively always "available" — returning true keeps the
 * handler from releasing early on host+srflx only and regressing calls
 * behind symmetric NATs/firewalls.
 */
export function hasCachedTurnServers(): boolean {
  if (!getAudioFlags().iceServersCacheAndPrefetch) return true;
  return !!(cachedIceServers?.some(s => s.username && s.credential));
}

/**
 * Fetch STUN/TURN server credentials from the calling service.
 * Returns a single ICE server entry built from the server response.
 * Falls back to public STUN servers on any error.
 */
export async function getIceServers(): Promise<IceServer[]> {
  // Variant A disables caching/prefetch, but still performs a fresh fetch of
  // TURN credentials on every call — matches pre-PR1516 behaviour. Without
  // TURN, sessions behind symmetric NAT/firewalls degrade to one-way or
  // no audio, which would be a worse regression than skipping cache.
  const cachingEnabled = getAudioFlags().iceServersCacheAndPrefetch;

  const callingServiceUrl = window.__ENV__?.URL_CALLING_SERVICE;
  const state = store.getState();
  const token = getAccessToken();
  const tenant = state.auth?.tenant;

  if (cachingEnabled && cachedIceServers && Date.now() < cacheExpiryMs) {
    logger.debug('Returning cached ICE servers', {
      source: 'iceServers',
      event: 'turn_fetch_cached',
      expiresInMs: cacheExpiryMs - Date.now(),
    });
    return cachedIceServers;
  }

  logger.info('Fetching TURN/STUN servers from calling service', {
    source: 'iceServers',
    event: 'turn_fetch_start',
    callingServiceUrl,
    hasTenant: !!tenant,
    hasToken: !!token,
  });

  if (!callingServiceUrl) {
    logger.warn('URL_CALLING_SERVICE not set — falling back to public STUN', {
      source: 'iceServers',
      event: 'turn_fetch_no_url',
    });
    return PUBLIC_STUN_FALLBACK;
  }

  const url = `${callingServiceUrl}/api/v1/turn/servers`;

  try {
    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: {
        ...(tenant && { tenant }),
      },
    });

    logger.info('TURN server fetch raw response', {
      source: 'iceServers',
      event: 'turn_fetch_raw_response',
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      url,
    });

    if (!response.ok) {
      const errorBody = await response.text().catch(() => '');
      logger.error('TURN server fetch HTTP error', {
        source: 'iceServers',
        event: 'turn_fetch_http_error',
        status: response.status,
        statusText: response.statusText,
        errorBody,
        url,
      });
      return PUBLIC_STUN_FALLBACK;
    }

    const data: TurnServerCredentials = await response.json();

    logger.info('TURN server credentials received', {
      source: 'iceServers',
      event: 'turn_fetch_success',
      uriCount: data.uris?.length,
      uris: data.uris,
      username: data.username,
      ttl: data.ttl,
      hasPassword: !!data.password,
    });

    const iceServers: IceServer[] = [
      { urls: data.uris, username: data.username, credential: data.password },
    ];

    if (cachingEnabled) {
      // Cache for 90% of TTL so we refresh before credentials actually expire
      cachedIceServers = iceServers;
      cacheExpiryMs = Date.now() + Math.floor(data.ttl * 900);
    }

    logger.info('ICE servers built from server response', {
      source: 'iceServers',
      event: 'ice_servers_set',
      count: data.uris?.length,
      ttl: data.ttl,
      iceServers,
    });

    return iceServers;
  } catch (error: any) {
    logger.error('TURN server fetch failed — falling back to public STUN', {
      source: 'iceServers',
      event: 'turn_fetch_failed',
      errorMessage: error?.message || String(error),
      errorName: error?.name,
      url,
    });
    return PUBLIC_STUN_FALLBACK;
  }
}

/**
 * Get ICE gathering timeout configuration
 * @returns Timeout value in milliseconds
 */
export function getIceGatheringTimeout(): number {
  return 5000; // 5 seconds - industry standard for ICE gathering
}
